import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Badge, Input, Select, Alert, Reveal, Skeleton, useIsMobile } from '../components/ui';
import AdminLayout from '../components/layout/AdminLayout';
import api from '../utils/api';

const ROLES = [
  ['', 'All roles'],
  ['donor', 'Donor'],
  ['recipient', 'Recipient'],
  ['organization', 'Organization'],
  ['admin', 'Admin'],
];

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [role, setRole] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [search, setSearch] = useState('');
  const [actioningId, setActioningId] = useState(null);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setUsers(null);
    setLoadError(false);
    const params = {};
    if (role) params.role = role;
    if (activeFilter) params.is_active = activeFilter === 'active';
    api.get('/admin/users', { params })
      .then(r => setUsers(r.data))
      .catch(() => setLoadError(true));
  }, [role, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!users) return null;
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.phone.includes(q)
    );
  }, [users, search]);

  const toggleActive = async (u) => {
    setActioningId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/${u.is_active ? 'deactivate' : 'activate'}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Something went wrong.');
    } finally {
      setActioningId(null);
    }
  };

  return (
    <AdminLayout>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>Users</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Search, filter, and manage user accounts.</p>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Input
          label="Search"
          placeholder="Name, email, or phone"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 0, minWidth: '200px', flex: isMobile ? '1 1 100%' : 'initial' }}
        />
        <Select label="Role" value={role} onChange={e => setRole(e.target.value)} style={{ marginBottom: 0, width: '160px' }}>
          {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Select label="Status" value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={{ marginBottom: 0, width: '140px' }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Deactivated</option>
        </Select>
      </div>

      <Card>
        {users === null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 0' }}>
                <div style={{ flex: 1 }}>
                  <Skeleton width="40%" height="13px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="55%" height="11px" />
                </div>
                <Skeleton width="90px" height="30px" radius="8px" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <Alert variant="error">Couldn't load users. Please try again.</Alert>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            <p style={{ fontSize: '13.5px' }}>No matching users.</p>
          </div>
        ) : filtered.map((u, i) => (
          <Reveal key={u.id} delay={Math.min(i, 6) * 30}>
            <div
              style={{
                display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px',
                padding: '13px 0',
                borderBottom: i < filtered.length - 1 ? '1px solid var(--gray-100)' : 'none',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>{u.name}</span>
                  <Badge color="gray">{u.role}</Badge>
                  <Badge color={u.is_active ? 'green' : 'red'}>{u.is_active ? 'Active' : 'Deactivated'}</Badge>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                  {u.email} · {u.phone}{u.city ? ` · ${u.city}` : ''}
                </div>
              </div>
              <Button
                size="sm"
                variant={u.is_active ? 'danger' : 'primary'}
                loading={actioningId === u.id}
                onClick={() => toggleActive(u)}
              >
                {u.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </Reveal>
        ))}
      </Card>
    </AdminLayout>
  );
}
